import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { money, fmtDate, resolveUrl } from "@/lib/format";
import { Printer, FileText, X } from "lucide-react";
import { StatusBadge } from "@/components/StatusBadge";

export function InvoiceDialog({ invoice, open, onClose }) {
  const [size, setSize] = useState("thermal"); // thermal | a4
  if (!invoice) return null;
  const sym = invoice.currency_symbol || "\u20b9";

  const doPrint = () => {
    document.body.classList.add(size === "thermal" ? "print-thermal" : "print-a4");
    window.print();
    setTimeout(() => document.body.classList.remove("print-thermal", "print-a4"), 500);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-md bg-brand-bg max-h-[92vh] overflow-y-auto" data-testid="invoice-dialog">
        <DialogHeader><DialogTitle className="font-serif text-2xl tracking-tight text-left">Invoice {invoice.invoice_number}</DialogTitle><DialogDescription className="sr-only">Invoice details and print options</DialogDescription></DialogHeader>

        <div className="flex gap-2 mb-2">
          <button onClick={() => setSize("thermal")} data-testid="size-thermal"
            className={`text-xs px-3 py-1.5 rounded-sm border ${size === "thermal" ? "bg-brand-primary text-white border-brand-primary" : "bg-white border-brand-line"}`}>80mm Thermal</button>
          <button onClick={() => setSize("a4")} data-testid="size-a4"
            className={`text-xs px-3 py-1.5 rounded-sm border ${size === "a4" ? "bg-brand-primary text-white border-brand-primary" : "bg-white border-brand-line"}`}>A4</button>
        </div>

        <div id="print-area" className={`bg-white border border-brand-line rounded-sm p-6 text-brand-text ${size === "thermal" ? "receipt-thermal" : "receipt-a4"}`}>
          <div className="text-center mb-4">
            {invoice.logo_url && <img src={resolveUrl(invoice.logo_url)} alt="logo" className="w-16 h-16 object-cover rounded-sm mx-auto mb-2" crossOrigin="anonymous" />}
            <p className="font-serif text-2xl tracking-tight">{invoice.club_name}</p>
            <p className="text-xs text-brand-muted">{invoice.club_address}</p>
          </div>
          <div className="flex justify-between text-xs mb-1"><span>Invoice</span><span className="font-semibold">{invoice.invoice_number}</span></div>
          <div className="flex justify-between text-xs mb-1"><span>Order</span><span>{invoice.order_number}</span></div>
          <div className="flex justify-between text-xs mb-1"><span>Member</span><span>{invoice.member_name}</span></div>
          {invoice.member_code && <div className="flex justify-between text-xs mb-1"><span>Member No/GSTIN (Buyer)</span><span>{invoice.member_code}</span></div>}
          {invoice.customer_group && <div className="flex justify-between text-xs mb-1"><span>Group</span><span>{invoice.customer_group}</span></div>}
          <div className="flex justify-between text-xs mb-1"><span>Date</span><span>{fmtDate(invoice.created_at)}</span></div>
          <div className="flex justify-between text-xs mb-1"><span>Mode</span><span className="uppercase">{invoice.payment_mode}</span></div>
          {invoice.gst_enabled && invoice.gstin && <div className="flex justify-between text-xs mb-3"><span>GSTIN (Club)</span><span>{invoice.gstin}</span></div>}

          <div className="border-t border-dashed border-brand-line pt-2">
            {invoice.items.map((it, i) => (
              <div key={i} className="flex justify-between text-xs py-0.5">
                <span>{it.quantity} × {it.name}</span>
                <span>{money(it.price * it.quantity, sym)}</span>
              </div>
            ))}
          </div>

          <div className="border-t border-dashed border-brand-line mt-2 pt-2 space-y-1 text-xs">
            <div className="flex justify-between"><span>Subtotal</span><span>{money(invoice.subtotal, sym)}</span></div>
            {invoice.item_discount_total > 0 && <div className="flex justify-between" data-testid="invoice-item-discount"><span>Item Discount{invoice.item_discount_percent ? ` (${invoice.item_discount_percent}%)` : ""}</span><span>-{money(invoice.item_discount_total, sym)}</span></div>}
            {invoice.group_discount_total > 0 && <div className="flex justify-between" data-testid="invoice-group-discount"><span>Group Discount{invoice.group_discount_percent ? ` (${invoice.group_discount_percent}%)` : ""}</span><span>-{money(invoice.group_discount_total, sym)}</span></div>}
            {(invoice.charges || []).filter((c) => c.amount > 0).map((c, i) => (
              <div key={i} className="flex justify-between" data-testid={`invoice-charge-${i}`}><span>{c.name}</span><span>{money(c.amount, sym)}</span></div>
            ))}
            {invoice.gst_enabled && invoice.gst_mode === "inter" && <div className="flex justify-between"><span>IGST ({invoice.gst_rate}%)</span><span>{money(invoice.igst, sym)}</span></div>}
            {invoice.gst_enabled && invoice.gst_mode !== "inter" && <>
              <div className="flex justify-between"><span>CGST</span><span>{money(invoice.cgst, sym)}</span></div>
              <div className="flex justify-between"><span>SGST</span><span>{money(invoice.sgst, sym)}</span></div>
            </>}
            <div className="flex justify-between font-bold text-sm border-t border-brand-line pt-1 mt-1"><span>TOTAL</span><span>{money(invoice.total, sym)}</span></div>
            <div className="flex justify-between"><span>Paid</span><span>{money(invoice.amount_paid, sym)}</span></div>
            <div className="flex justify-between"><span>Balance Due</span><span>{money(invoice.balance_due, sym)}</span></div>
          </div>

          <div className="mt-3 flex justify-center"><StatusBadge status={invoice.cancelled ? "cancelled" : invoice.status} /></div>
          <p className="text-center text-[10px] text-brand-muted mt-4">{invoice.invoice_footer}</p>
        </div>

        <div className="flex gap-2 no-print">
          <Button onClick={onClose} data-testid="invoice-close-button" variant="outline" className="rounded-sm border-brand-line gap-2">
            <X className="w-4 h-4" /> Close
          </Button>
          <Button onClick={doPrint} data-testid="print-invoice-button" className="flex-1 bg-brand-primary hover:bg-brand-primary-hover text-white rounded-sm gap-2">
            <Printer className="w-4 h-4" /> Print / Save PDF
          </Button>
        </div>
        <p className="text-[10px] text-brand-muted flex items-center gap-1 no-print"><FileText className="w-3 h-3" /> Use "Save as PDF" in the print dialog to download.</p>
      </DialogContent>
    </Dialog>
  );
}
